import * as React from 'react';
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  ListSubheader,
  TextField,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 300,
    },
  },
  disableAutoFocusItem: true,
};

const getInitials = (fullName) => {
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function TruncatedTooltip({ children }) {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [children]);

  return isTruncated ? (
    <Tooltip title={children}>
      <Typography
        ref={textRef}
        noWrap
        sx={{
          maxWidth: '160px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '14px',
          color: '#111',
          cursor: 'default',
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  ) : (
    <Typography
      ref={textRef}
      noWrap
      sx={{
        maxWidth: '160px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '14px',
        color: '#111',
      }}
    >
      {children}
    </Typography>
  );
}

export default function GroupedSelect({
  label = 'Select Items',
  options = [],
  selectedValues = [],
  onChange = () => { },
}) {
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const filteredOptions = options.filter((name) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <FormControl sx={{ m: 0, width: { xl: '240px', lg: '190px', md: '190px', sm: '190px' } }}>
      <InputLabel id={`${label}-label`} sx={{
        top: '-7px',
        fontSize: '14px',
        // Target when label is focused
        '&.Mui-focused': {
          top: '0px',
          fontSize: { xl: '16px', lg: '13px', md: '13px', sm: '13px', xs: '13px' },
        },
        // Target when input is filled (label shrinks)
        '&.MuiFormLabel-filled': {
          top: '0px',
          fontSize: { xl: '16px', lg: '13px', md: '13px', sm: '13px', xs: '13px' },
        },
      }}>{label}</InputLabel>
      <Select
        MenuProps={MenuProps}
        labelId={`${label}-label`}
        id={`${label}-select`}
        multiple
        value={selectedValues}
        onChange={handleChange}
        input={<OutlinedInput label={label} sx={{
          height: 38,
          borderRadius: '50px',
          paddingRight: '32px',
          display: 'flex',
          alignItems: 'center',
        }} />}
        IconComponent={KeyboardArrowDown}
        renderValue={(selected) => selected.join(', ')}

        sx={{
          background: '#fff',
          borderRadius: '50px',
          '& .MuiSelect-select': {
            paddingTop: '8px',
            paddingBottom: '7px',
            height: 23,
            fontSize: '14px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '50px',
          },
          '& .MuiSelect-icon': {
            color: '#64748B',
            right: '8px',
          },
        }}
      >

        <ListSubheader>
          <Box
            disableRipple
            disableTouchRipple
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 999,
              background: '#fff !important',
            }}
          >
            <TextField
              sx={{
                background: '#fff', '& .MuiInputBase-input': {
                  fontSize: '13px',
                },
              }}
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            />
          </Box>
        </ListSubheader>



        {filteredOptions.map((name) => (
          <MenuItem key={name} value={name}>
            <Checkbox
              checked={selectedValues.includes(name)}
              icon={
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    border: '1px solid #D0D5DD',
                    backgroundColor: '#E5E8EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <filter
                        id="shadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="1"
                          stdDeviation="1"
                          floodColor="black"
                          floodOpacity="0.4"
                        />
                      </filter>
                    </defs>
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="url(#shadow)"
                    />
                  </svg>
                </Box>
              }
              checkedIcon={
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              }
            />

            <Box
              sx={{
                minWidth: 35,
                maxWidth: 35,
                height: 35,
                borderRadius: '50%',
                backgroundColor: '#1976d2',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
              }}
            >
              {getInitials(name)}
            </Box>

            <TruncatedTooltip>{name}</TruncatedTooltip>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}